import styled from 'styled-components';
import { sendGoogleAnalyticsEvents } from '@elfsight/lib-google-analytics';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import { Avatar, DateTime, ellipsisMixin } from '@elfsight/widget-ui';
import { getLineHeight } from '@elfsight/widget-utils';
import type { DataValue } from '../../data/sources';
import { Link, VerifiedBadge } from '../../lib/components';
import { useSocialFeedSettings } from '../../utils';
import { usePost } from '../Post';
import { usePostSettings } from '../utils';

const IMAGE_SIZE = 32;

interface UserProps {
  user: DataValue['user'];
  timestamp: number;
  showName: boolean;
  showBadge: boolean;
  showPicture: boolean;
  showDate: boolean;
  color: string;
  center: boolean;
}

export function User({
  user,
  timestamp,
  showName,
  showBadge,
  showPicture,
  showDate,
  color,
  center,
  ...forwardingProps
}: UserProps) {
  const {
    language,
    postTextColor,
    googleAnalytics = [],
    widgetId = '',
    editMode,
    font,
    tooltipBackgroundColor,
    tooltipTextColor
  } = useSocialFeedSettings();
  const { id, url, source } = usePost();
  const { postVerifiedBadgeColor } = usePostSettings();

  const handleUserProfileClick = () => {
    sendGoogleAnalyticsEvents({
      eventType: 'socialPlatformOpened',
      googleAnalyticsEvents: googleAnalytics,
      editMode,
      valueMapping: {
        widgetId,
        postId: id,
        postUrl: url || '',
        entryPoint: 'post'
      }
    });
  };

  const isLinkedin = source?.type === 'linkedin';
  const badgeColor = isLinkedin
    ? {
        ...postVerifiedBadgeColor,
        color: getColorWithAlpha(postTextColor, 0.7)
      }
    : postVerifiedBadgeColor;

  const locale = language?.language || 'en';
  const { image: imageSrc, name, username, verified, url: link } = user ?? {};

  const processedUsername = username ? `@${username}` : undefined;
  const finalName = showName ? name || processedUsername : undefined;

  const finalShowUsername = showName && !!username && !!name;
  const finalShowDate = showDate && !!timestamp;

  return (
    <Container
      {...forwardingProps}
      _textColor={color}
      _textAlign={center && !showPicture ? 'center' : 'left'}
    >
      {showPicture && (
        <AvatarLink
          href={link}
          aria-label={`Visit ${finalName || 'user'}'s profile on ${
            source?.title || 'social media'
          }`}
          onClick={handleUserProfileClick}
        >
          <StyledAvatar src={imageSrc} size={IMAGE_SIZE} altText={finalName} />
        </AvatarLink>
      )}

      <Info
        style={{
          maxWidth: showPicture ? `calc(100% - ${IMAGE_SIZE + 8}px)` : '100%'
        }}
      >
        {finalName && (
          <NameLink href={link} onClick={handleUserProfileClick}>
            <NameAndBadge>
              <Name>{finalName}</Name>

              {verified && showBadge && <VerifiedBadge color={badgeColor} />}
            </NameAndBadge>
          </NameLink>
        )}

        <Caption>
          {finalShowUsername && <span>{`@${username}`}</span>}
          {finalShowDate && (
            <span>
              <StyledDateTime
                value={timestamp}
                locale={locale}
                tooltipFont={font}
                tooltipBackgroundColor={tooltipBackgroundColor}
                tooltipFontColor={tooltipTextColor}
              />
            </span>
          )}
        </Caption>
      </Info>
    </Container>
  );
}

const FONT_SIZE = 13;

const StyledAvatar = styled(Avatar)`
  margin-right: 8px;
`;

const Info = styled.div`
  flex-grow: 0;
`;

const Name = styled.div`
  font-weight: bold;
  flex-grow: 0;
  ${ellipsisMixin};
`;

const NameLink = styled(Link)`
  display: inline-block;
  position: relative;
  max-width: 100%;
`;

const NameAndBadge = styled.div`
  display: inline-flex;
  gap: 4px;
  align-items: center;
  position: relative;
  max-width: 100%;
`;

const AvatarLink = styled(Link)``;

const Container = styled.div<{
  _textAlign: 'center' | 'left';
  _textColor: string;
}>`
  display: flex;
  align-items: center;
  font-size: ${FONT_SIZE}px;
  line-height: ${getLineHeight({ fontSize: FONT_SIZE })};
  text-align: ${({ _textAlign }) => _textAlign};
  color: ${({ _textColor }) => _textColor};
  max-width: 100%;
`;

const StyledDateTime = styled(DateTime)`
  text-overflow: ellipsis;
  overflow: hidden;
`;

const Caption = styled.div`
  opacity: 0.7;
  ${ellipsisMixin};

  & span:not(:last-child)::after {
    content: '\\00B7';
    margin: 0 4px;
  }
`;
